<template>
  <div class="chart-engineer-wrapper">
    <!-- Loading State -->
    <div
      v-if="chartLoading"
      class="chart-loading-card"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Loading"
      />
      <span class="loading-text">Loading branch analytics...</span>
    </div>

    <!-- Empty State (after first load) -->
    <div
      v-else-if="!chartLoading && hasLoaded && isEmpty === 0"
      class="empty-state-card"
    >
      <div class="empty-state-content">
        <div class="empty-state-icon">
          <feather-icon
            icon="BarChart2Icon"
            size="48"
          />
        </div>
        <h4 class="empty-state-title">
          No engineers in this branch
        </h4>
        <p class="empty-state-text">
          No engineers are available in this branch yet. Select a different branch or add engineers to view the allocation chart.
        </p>
        <div class="empty-state-hint">
          <feather-icon
            icon="InfoIcon"
            size="16"
          />
          <span>Click on a branch from the sidebar to see its data</span>
        </div>
      </div>
    </div>

    <!-- Chart Content -->
    <template v-else>
      <!-- Stats Summary -->
      <div class="chart-stats">
        <div class="stat-item">
          <div class="stat-icon stat-icon-engineers">
            <feather-icon
              icon="UsersIcon"
              size="20"
            />
          </div>
          <div class="stat-details">
            <span class="stat-value">{{ labelsData.length }}</span>
            <span class="stat-label">Engineers</span>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon stat-icon-sources">
            <feather-icon
              icon="LayersIcon"
              size="20"
            />
          </div>
          <div class="stat-details">
            <span class="stat-value">{{ seriesData.length }}</span>
            <span class="stat-label">Sources</span>
          </div>
        </div>
        <div class="stat-item stat-item-hint">
          <feather-icon
            icon="MousePointerIcon"
            size="18"
          />
          <span>Click bars or names for details</span>
        </div>
      </div>

      <!-- Chart Area -->
      <div class="chart-container">
        <div class="chart-inner">
          <apexchart
            :key="isDarkMode"
            type="bar"
            :options="chartOptions"
            :series="seriesData"
            @dataPointSelection="handlePointClick"
          />
        </div>
      </div>
    </template>

    <!-- Popup Table -->
    <TableRequestForEngineer
      v-if="showPopupTable"
      :user-data="userData"
      @close="showPopupTable = false"
    />
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import TableRequestForEngineer from './table-user/TableRequestForEngineer.vue'

export default {
  components: {
    apexchart: VueApexCharts,
    TableRequestForEngineer,
  },

  props: {
    dataChart: {
      type: Object,
      default: () => ({}),
    },
    chartLoading: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      xAxisListenersAdded: false,
      showPopupTable: false,
      userData: {},
      isDarkMode: false,
      themeObserver: null,
      hasLoaded: false,
    }
  },

  computed: {
    seriesData() {
      return this.dataChart.source || []
    },

    isEmpty() {
      return this.dataChart?.engineers_name?.length || 0
    },

    labelsData() {
      return this.dataChart.engineers_name || []
    },

    chartColors() {
      const colorMap = {
        jeem1: '#6366f1',
        moiat: '#22c55e',
        saber: '#f97316',
        sfda: '#06b6d4',
        sls: '#ef4444',
        goeic: '#0d9488',
        'sfda-eg': '#ec4899',
        moph: '#84cc16',
      }
      const fallbacks = ['#8b5cf6', '#14b8a6', '#f59e0b', '#d946ef']
      let fallbackIdx = 0

      return this.seriesData?.map(
        /* eslint-disable-next-line */
        s => colorMap[s.name] || fallbacks[fallbackIdx++ % fallbacks.length],
      )
    },

    chartOptions() {
      const textColor = this.isDarkMode ? '#e0e2e6' : '#5e5873'
      const gridColor = this.isDarkMode ? '#3b4253' : '#ebe9f1'

      return {
        chart: {
          height: Math.max(380, this.labelsData.length * 48),
          type: 'bar',
          stacked: true,
          background: 'transparent',
          foreColor: textColor,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
          events: {
            mounted: () => this.addLabelListeners(),
            updated: () => {
              this.xAxisListenersAdded = false
              this.$nextTick(() => this.addLabelListeners())
            },
          },
        },

        theme: {
          mode: this.isDarkMode ? 'dark' : 'light',
        },

        colors: this.chartColors,

        plotOptions: {
          bar: {
            horizontal: true,
            barHeight:
              this.labelsData.length >= 1 && this.labelsData.length <= 5
                ? '40%'
                : '75%',
            borderRadius: 8,
            borderRadiusApplication: 'end',
            dataLabels: {
              position: 'center',
            },
          },
        },

        dataLabels: {
          enabled: false,
        },

        stroke: {
          show: true,
          width: 1,
          colors: ['transparent'],
        },

        grid: {
          borderColor: gridColor,
          strokeDashArray: 4,
          xaxis: {
            lines: { show: true },
          },
          yaxis: {
            lines: { show: false },
          },
        },

        xaxis: {
          categories: this.labelsData,
          labels: {
            style: {
              colors: textColor,
              fontSize: '12px',
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },

        yaxis: {
          labels: {
            style: {
              colors: textColor,
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },

        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          offsetY: 8,
          itemMargin: {
            horizontal: 12,
          },
          labels: {
            colors: textColor,
          },
        },

        tooltip: {
          theme: this.isDarkMode ? 'dark' : 'light',
          x: {
            formatter(val) {
              return `${val} requests`
            },
          },
        },

        fill: {
          opacity: 1,
        },
      }
    },
  },

  watch: {
    chartLoading(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.hasLoaded = true
      }
    },
    dataChart: {
      handler() {
        this.xAxisListenersAdded = false
        this.$nextTick(() => this.addLabelListeners())
      },
      deep: true,
      immediate: true,
    },

    isDarkMode() {
      this.xAxisListenersAdded = false
      this.$nextTick(() => this.addLabelListeners())
    },
  },

  mounted() {
    this.observeTheme()
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  },

  methods: {
    observeTheme() {
      const { body } = document
      this.isDarkMode = body.classList.contains('dark-layout')

      this.themeObserver = new MutationObserver(() => {
        const newMode = body.classList.contains('dark-layout')
        if (newMode !== this.isDarkMode) {
          this.isDarkMode = newMode
        }
      })

      this.themeObserver.observe(body, {
        attributes: true,
        attributeFilter: ['class'],
      })
    },

    handlePointClick(event, chartContext, config) {
      const indexName = config.dataPointIndex
      const indexSource = config.seriesIndex

      const engineerName = this.labelsData[indexName]
      const engineerSource = this.dataChart.source?.[indexSource]?.name
      const engineerId = this.dataChart.user_ids?.[indexName]

      if (engineerName && engineerSource) {
        this.$emit('show-popup', {
          name: engineerName,
          source: engineerSource,
          userID: engineerId,
        })
      }
    },

    addLabelListeners() {
      if (this.xAxisListenersAdded) return

      const yEls = document.querySelectorAll('.apexcharts-yaxis-texts-g text')
      const xEls = document.querySelectorAll('.apexcharts-xaxis-texts-g text')
      const elements = yEls.length === this.labelsData.length
        ? yEls
        : xEls

      const accentColor = this.isDarkMode ? '#9e95f5' : '#7367f0'
      const normalColor = this.isDarkMode ? '#e0e2e6' : '#5e5873'

      const labelCount = this.labelsData.length
      elements.forEach((element, i) => {
        if (i >= labelCount) return
        const el = element
        el.style.cursor = 'pointer'
        el.setAttribute('fill', normalColor)
        el.addEventListener('mouseenter', () => el.setAttribute('fill', accentColor))
        el.addEventListener('mouseleave', () => el.setAttribute('fill', normalColor))
        el.addEventListener('click', () => {
          this.userData = {
            name: this.labelsData[i],
            userID: this.dataChart.user_ids?.[i],
          }
          this.showPopupTable = true
        })
      })

      this.xAxisListenersAdded = true
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-engineer-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
}

.chart-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  padding: 2.5rem 1.5rem;

  .loading-text {
    margin-top: 0.75rem;
    font-size: 0.95rem;
    color: #b9b9c3;
  }
}

/* Empty State */
.empty-state-card {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.06) 0%, rgba(158, 149, 245, 0.04) 100%);
  border: 2px dashed rgba(115, 103, 240, 0.25);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-state-content {
  max-width: 400px;
  margin: 0 auto;
}

.empty-state-icon {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.2) 0%, rgba(158, 149, 245, 0.15) 100%);
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.empty-state-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 0.5rem;
}

.empty-state-text {
  font-size: 0.95rem;
  color: #b9b9c3;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.empty-state-hint {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(115, 103, 240, 0.08);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #7367f0;

  svg {
    flex-shrink: 0;
  }
}

/* Stats Summary */
.chart-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 6px rgba(34, 41, 47, 0.04);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(34, 41, 47, 0.08);
  }
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.stat-icon-engineers {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  &.stat-icon-sources {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }
}

.stat-details {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #5e5873;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-item-hint {
  margin-left: auto;
  background: rgba(115, 103, 240, 0.06);
  border-color: rgba(115, 103, 240, 0.15);
  color: #7367f0;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Chart Container */
.chart-container {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  overflow: hidden;
}

.chart-inner {
  padding: 1rem;
}

::v-deep(.apexcharts-xaxis-texts-g text),
::v-deep(.apexcharts-yaxis-texts-g text) {
  cursor: pointer !important;
  transition: fill 0.2s ease;
}
</style>
